import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ChipSelect } from 'src/components/ChipSelect/ChipSelect';
import { CaretIcon } from 'src/components/Icons/icons';
import { TextIcon } from 'src/components/Icons/TextIcon';
import { OrganizationGroupFilterLevel } from 'src/view/hooks/useOrganizationGroupFilters';

type GroupFilterControlsProps = {
    levels: OrganizationGroupFilterLevel[];
    selectedFilterCount: number;
    onChange: (levelId: string, nextIds: string[]) => void;
};

export const GroupFilterControls = ({
    levels,
    selectedFilterCount,
    onChange,
}: GroupFilterControlsProps) => {
    const { t } = useTranslation();
    const [isExpanded, setIsExpanded] = useState(false);

    if (!levels.length) {
        return null;
    }

    return (
        <>
            <div className="mb-4 hidden gap-3 md:grid md:grid-cols-2">
                {levels.map(level => (
                    <ChipSelect
                        key={level.id}
                        label={level.name}
                        options={level.options}
                        selectedIds={level.selectedIds}
                        onChange={nextIds => onChange(level.id, nextIds)}
                        placeholder={t(
                            'modules.messages.filters.levelPlaceholder',
                            {
                                levelName: level.name.toLocaleLowerCase(),
                            },
                        )}
                    />
                ))}
            </div>
            <div className="relative z-20 mb-4 rounded-2xl border border-starkWhite bg-white shadow-sm md:hidden">
                <button
                    type="button"
                    className="flex w-full items-center gap-3 px-4 py-3 text-left"
                    onClick={() => setIsExpanded(current => !current)}
                >
                    <span className="flex-1 font-medium text-woodyBrown">
                        {t('modules.messages.filters.title')}
                        {selectedFilterCount > 0
                            ? ` (${selectedFilterCount})`
                            : ''}
                    </span>
                    <div
                        className={`text-woodyBrown text-opacity-50 transition-transform duration-200 ${
                            isExpanded ? 'rotate-180' : 'rotate-0'
                        }`}
                    >
                        <TextIcon SVG={CaretIcon} />
                    </div>
                </button>
                <div
                    className={`transition-all duration-300 ${
                        isExpanded
                            ? 'max-h-[1200px] overflow-visible opacity-100'
                            : 'max-h-0 overflow-hidden opacity-0'
                    }`}
                >
                    <div className="grid gap-3 px-4 pb-4">
                        {levels.map(level => (
                            <ChipSelect
                                key={level.id}
                                label={level.name}
                                options={level.options}
                                selectedIds={level.selectedIds}
                                onChange={nextIds =>
                                    onChange(level.id, nextIds)
                                }
                                placeholder={t(
                                    'modules.messages.filters.levelPlaceholder',
                                    {
                                        levelName:
                                            level.name.toLocaleLowerCase(),
                                    },
                                )}
                            />
                        ))}
                    </div>
                </div>
            </div>
        </>
    );
};
